import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { IngestStatusTypes } from '../../../../interfaces';
import { useGetIngestJobStatusQuery } from '../../../../services/ingest.service';
import { config } from 'apps/ng/src/app/config';
import { CircularLoader } from 'apps/ng/src/app/shared';

interface IngestStatusProps {
  ingestId?: string | null;
  message?: string | null;
  customInterval?: number;
  onComplete?: (data: IngestJob) => void;
  onError: (error: IngestError) => void;
}

export type IngestError = {
  type: 'unknown' | 'fatal' | 'validation';
  error: string;
};

const StyledContainer = styled.div`
  width: 100%;
  flex-direction: column;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;

  text-align: center;
  line-height: 1.3;
`;

export const IngestStatus: FC<IngestStatusProps> = ({
  message,
  ingestId,
  customInterval = 600,
  onComplete,
  onError,
}) => {
  // @todo display errors
  const [ingestError, setIngestError] = useState<IngestError | null>({ type: 'unknown', error: '' });

  void ingestError;

  const pollingAbortController = useRef<AbortController>(new AbortController());

  const {
    data: ingestJob,
    refetch,
    error,
  } = useGetIngestJobStatusQuery({ ingestJobId: ingestId! }, { skip: !ingestId });

  useEffect(() => {
    if (!ingestJob || pollingAbortController.current.signal.aborted) return;

    switch (ingestJob.status) {
      case IngestStatusTypes.PROCESSING:
        break;

      case IngestStatusTypes.SUCCESS:
        {
          pollingAbortController.current.abort();
          onComplete?.(ingestJob);
        }
        break;

      case IngestStatusTypes.ERROR:
        {
          pollingAbortController.current.abort();

          if (!onError) break;
          if (ingestJob.result_status) {
            if (!['validation', 'unknown', 'fatal'].includes(ingestJob.result_status.type) || !ingestJob.result_status.error) {
              const ingestError: IngestError = {
                type: 'unknown',
                error: `Unhandled error type: ${ingestJob.result_status.type}`,
              };
              console.warn(`Unknown ingest job error type: ${ingestJob.result_status.type}`);
              setIngestError(ingestError);
              onError(ingestError);

              break;
            }
            setIngestError(ingestJob.result_status);
            onError(ingestJob.result_status);
          } else {
            const ingestError: IngestError = {
              type: 'unknown',
              error: 'Failed to process workbook. Please try again later.',
            };
            setIngestError(ingestError);
            onError(ingestError);
          }
        }
        break;

      default:
        {
          console.warn('Unhandled ingestJob status:', ingestJob.status);
          pollingAbortController.current.abort();
          onError?.({ type: 'unknown', error: 'Unexpected status received.' });
        }
        break;
    }
  }, [ingestJob]);

  useEffect(() => {
    if (!error) return;

    pollingAbortController.current.abort();
    onError?.({ type: 'unknown', error: 'Failed to get status.' });
    // @todo handle api errors
    // onError?.({ type: 'unknown', error: error })
  }, [error]);

  useEffect(() => {
    if (!ingestId) return; // don't start polling until we have a valid ID

    setIngestError(null);

    pollingAbortController.current.abort();
    pollingAbortController.current = new AbortController();

    let pollingInterval: number;
    let pollingTimeout: number;

    const stopPolling = () => {
      window.clearInterval(pollingInterval);
      window.clearTimeout(pollingTimeout);
    };

    pollingAbortController.current.signal.addEventListener('abort', stopPolling, { once: true });

    // only start polling when the query is actually active
    pollingInterval = window.setInterval(() => {
      refetch();
    }, customInterval);

    pollingTimeout = window.setTimeout(() => {
      stopPolling();
      onError?.({ type: 'unknown', error: 'Failed to upload workbook. Processing takes too long time.' });
    }, config.pollingTimeout);

    return stopPolling;
  }, [ingestId, refetch]);

  return (
    <StyledContainer>
      <h3>{ingestJob ? ingestJob.user_status : message}</h3>

      {/* <CircularProgressWithLabel value={0} /> */}
      <CircularLoader />
    </StyledContainer>
  );
};
